import { alpha, Box, Button, Card, CardContent, CardHeader, Divider, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Grid, IconButton, MenuItem, Select, Stack, Switch, Tooltip, Typography, useTheme } from "@mui/material"
import { Identifier, ListContextProvider, ReactAdminComponentPropsWithId, ReduxState, ShowBase, useDeleteWithConfirmController, useEditContext, useGetMainList, useListContext, useRecordContext, useRefresh, useSaveContext, useShowContext } from "ra-core"
import { FC, SyntheticEvent, useCallback, useEffect, useRef, useState } from "react"
import { Content } from "~/components/content/Content"
import { useDatabaseSelectCard, useAllDatabasesSource } from "~/components/databases/DatabaseSelectCard"
import { FunctionField, TextField } from "~/components/field"
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import ReplayIcon from '@mui/icons-material/Replay';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import DateField2 from "~/components/field/DateField2"
import { Datagrid } from "~/components/list/datagrid"
import Scrollbar from "~/components/Scrollbar"
import Confirm from "~/components/widgets/Confirm"
import CreateOrEdit from "~/controller/CreateOrEdit"
import { RecPatch } from "~/dataProvider/tools"
import { DatabaseResponseDto } from "~/gen/api/backup"
import { OdResourcePageProps } from "~/types/resource"
import EditKey from "../settings/encryption/keys/EditTVKey"
import { decodeHashid, genUrlWithHashid } from "~/utils/hashid"
import useLocalListController from "~/components/list/useLocalListController"
import { DataAccessPolicyResponseDto, EncryptionKeyResponseDto } from "~/gen/api/encrypt"
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers"
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { useSimpleSaveAction } from "~/controller/SaveContextProvider"
import Edit from "~/controller/Edit"
import { useDispatch, useSelector } from "react-redux"
import { push } from "connected-react-router"
import { pages, sorts } from "~/utils/paramsWithGetMainList"
import Create from "~/controller/Create"
import { usePeekEncryptSettingChangeLocked } from "~/components/obfuscation/usePeekEncryptSettingChange"
import { EncryptedDatabasesContextProvider } from "~/components/databases/useEncryptedDatabases"
import { getUserProfile } from "~/reducers/userProfile"
import { OnDataFeature } from "~/gen/api/user"
import { useHasPermit } from "~/authProvider/useHasPermit"
import { KEY_MANAGEMENT } from "~/constants"
import { disabledColor } from "~/utils/a11y"
import { nonSqlDbTypes } from "~/components/obfuscation/defs"


const dataAccessKeyListOptions : {sorters: Record<string, (a:DataAccessPolicyResponseDto & RecPatch, b:DataAccessPolicyResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id - b.id,
  }
}

const DatabaseKeysPolicy: FC<ReactAdminComponentPropsWithId> = (props) => {
  const { resource, match, ...rest } = props
  const id = decodeHashid(match ? (match.params as any)['id'] : '')

  const listCtrl= useLocalListController({ resource, basePath: '', filter: { databaseId: id } }, dataAccessKeyListOptions)
  
  return (
    <ShowBase id={id} resource="databases" {...rest} mutationMode={'pessimistic'} >
      <ListContextProvider value={listCtrl}>
        <EncryptedDatabasesContextProvider>
          <DatabaseKeysPolicyBody {...props} />
        </EncryptedDatabasesContextProvider>
      </ListContextProvider>
    </ShowBase>
  )
}

const policyResource = 'token-validation-keys'

interface PolicyOverride {
  id: Identifier,
  isOverridden: boolean,
}

const PolicyOverrideStatus: FC = () => {
  const hasWritePermit = useHasPermit([KEY_MANAGEMENT])
  const { record: policyOverride, loading, saving, refetch } = useEditContext<PolicyOverride>()
  const switchOverrideState = useSimpleSaveAction()

  const { total } = useListContext()

  const isOverride = policyOverride?.isOverridden || false
  return (
    <Box sx={{ m: 2 }}>
      <FormControl component="fieldset" variant="standard" sx={{ mx: 2 }}>
        <FormLabel component="legend">Data Access Policy Override</FormLabel>
        <FormGroup sx={{ '& span.MuiTouchRipple-root': { color: 'primary.main' } }}>
          <FormControlLabel 
            label="Override for this database"
            control={
              <Switch 
                disabled={!hasWritePermit}
                checked={isOverride} 
                onChange={(e:any) => {
                  if (hasWritePermit && !loading && !saving) {
                    switchOverrideState(e, { onSuccess: refetch })
                  }
                }} 
              />
            }
          />
        </FormGroup>
        <FormHelperText>If overridden, the Data Access Policies will not be enforced for this database. Encrypted data in this database will be automatically decrypted for application users without validating the user tokens.</FormHelperText>
      </FormControl>
      <Card variant="outlined" sx={{ bgcolor: 'info.light', mt: 4, px: 3, py: 2 }}>
        <Typography variant="subtitle2" sx={{ color: 'info.contrastText' }}>
          Data Access Policies {isOverride ? 'are bypassed' : 'are enforced'} for this database.{" "}
          {isOverride ? null : (
            total === 0 ? 
            '' : 
            'The following Token Validation Keys are currently used.'
          )}          
        </Typography>
      </Card>
    </Box>
  )
}



const DatabaseKeysPolicyBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([KEY_MANAGEMENT])
  const { record: databaseInfo } = useShowContext<DatabaseResponseDto & RecPatch>()
  const DatabaseSelectCard = useDatabaseSelectCard(useAllDatabasesSource)
  const userProfile = useSelector(getUserProfile)
  const isNoSql = nonSqlDbTypes.has(databaseInfo?.databaseType!)
  
  const dispatch = useDispatch()
  const handleChange = useCallback((e: any, chosenDb?: DatabaseResponseDto) => {
    if (chosenDb?.databaseId) {
      dispatch(push(
        genUrlWithHashid(`/databases/$0/keys-policy`, chosenDb.databaseId || 0, chosenDb.name)))
    }
  }, [])

  const listCtx = useListContext<DataAccessPolicyResponseDto & RecPatch>()

  const [states, setStates] = useState({
    openEdit: false,
    recordToEdit: null as DataAccessPolicyResponseDto | null,
    keyToRegenerate: null as DataAccessPolicyResponseDto | null,
  })

  const refresh = useRefresh()
  const refreshLight = useCallback(() => refresh(), [refresh])
  const savedHandlers = useDefaultSavedHandlers(() => `${!!states.recordToEdit ? 'Edit' : 'Create'} Token Validation Key`)

  const recordToDel = useRef<DataAccessPolicyResponseDto&RecPatch | null>(null)
  const delHandlers = useDefaultSavedHandlers('Delete Token Validation Key')
  delHandlers.onSuccess.merge(refreshLight)
  const {
    open: openConfirm,
    handleDelete,
    loading,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    record: recordToDel.current as any,
    resource: policyResource,
    ...delHandlers,
  })
  
  const isOverride = 
    useSelector<ReduxState, boolean|undefined>(store => 
      store.admin.resources['database-policy-override']
        .data[databaseInfo?.databaseId || -1]?.isOverridden
    )
  const handlers = useDefaultSavedHandlers('Update Data Access Policy Override')
  
  const encryptKeyHandlers = useDefaultSavedHandlers('Change Encryption Key')
  const regenTVKHandlers = useDefaultSavedHandlers(`Regenerate managed Database Token Validation Key`)


  const theme = useTheme()
  const rowStyles = (d: DataAccessPolicyResponseDto&RecPatch) => {
    const bgcolor = 
      d.isManaged ? alpha(theme.palette.warning.light, 0.1)
      : d.databaseId === 0 ? alpha(theme.palette.info.light, 0.1)
      : null
    return bgcolor ? { backgroundColor: bgcolor } : {}
  }
  return (
    <Content 
      pageName="Database Keys and Policy" 
      resourceName="Keys & Policy" 
      breadcrumbs={[
        ...(options?.breadcrumbs || []), 
        {
          label: 'Database Detail', 
          path: genUrlWithHashid(`/databases/$0/show`, databaseInfo?.databaseId || -1, databaseInfo?.name)
        },
      ]}
    >
      <Grid container spacing={4}>
        <Grid
          item
          xs={12}
        >
          <DatabaseSelectCard preSelectDbId={databaseInfo?.databaseId} onChange={handleChange} showDesc={true} />
        </Grid>
        
        <Grid item xs={12}>
          <Edit 
            id={databaseInfo?.id} 
            resource={databaseInfo ? 'settings-encrypt-key-assign' : 'noop'} 
            basePath="" 
            mutationMode="pessimistic"
            {...encryptKeyHandlers}
          >
            <DatabaseEncryptionKey />
          </Edit>
        </Grid>

        {!isNoSql && userProfile && userProfile.features?.some(f => f.featureId === OnDataFeature.OnDataDriver) &&
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title={
                  <>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      Token Validation Key
                      <Tooltip title="This key will be used to validate user tokens from applications. If no key is configured here, the Token Validation Keys configured at the organization level will be used.">
                        <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                      </Tooltip>
                    </Box>
                  </>
                }
                action={
                  hasWritePermit &&
                  <Button 
                    disabled={isOverride}
                    variant="contained" 
                    color="primary" 
                    size="small" 
                    startIcon={<AddIcon />}
                    onClick={() => setStates(v => ({...v, openEdit:true, recordToEdit:null})) }
                  >
                    New Key
                  </Button>
                }
              />
              <Divider />
              <Edit 
                resource={(databaseInfo?.databaseId) ? 'database-policy-override' : 'noop'}
                id={databaseInfo?.databaseId || -1}
                basePath=""
                mutationMode="pessimistic"
                {...handlers}
              >
                <PolicyOverrideStatus />
              </Edit>
              <Scrollbar>
                <Box sx={{ minWidth: 1150 }}>
                  <Datagrid 
                    hasBulkActions={false} 
                    sx={{ 
                      '& th' : { py: 2 },
                      '& td' : { py: 2 },
                      '& .description': { maxWidth: 500, display: 'inline-block', wordBreak: 'break-word' },
                    }}
                    rowStyle={rowStyles}
                    {...listCtx}
                  >
                    <DateField2 source="createdTime" label="Created Time" sortable={false} showTime />
                    <TextField source="description" label="Description" sortable={false} className="description" />
                    <FunctionField 
                      label="Action" 
                      sortable={false} 
                      render={(d?:DataAccessPolicyResponseDto&RecPatch) => {
                        if (!d) { return null }
                        return (
                          <>
                            {(!hasWritePermit || d.databaseId === 0) ? null
                            : d.isManaged 
                              ? <Tooltip title="Regenerate">
                                  {isOverride
                                    ? <ReplayIcon fontSize="small" />
                                    : <IconButton
                                        sx={{ color: 'text.primary' as any, '&:hover': { backgroundColor: 'action.hover' }, }}
                                        onClick={(e) => {
                                          setStates(v => ({...v, keyToRegenerate: d}))
                                          e.stopPropagation()
                                        }}
                                      >
                                        <ReplayIcon fontSize="small" />
                                      </IconButton>
                                  }
                                </Tooltip>
                              : <Tooltip title="Delete">
                                  {isOverride
                                    ? <DeleteForeverIcon fontSize="small" color="disabled" sx={{ p: 1 }} />
                                    : <IconButton
                                        sx={{
                                          color: 'text.primary' as any,
                                          '&:hover': { backgroundColor: 'action.hover' },
                                        }}
                                        onClick={(e:any) => {
                                          recordToDel.current = d as any
                                          handleDialogOpen(e)
                                        }}
                                      >
                                        <DeleteForeverIcon fontSize="small" />
                                      </IconButton>
                                  }
                                </Tooltip>}
                            <Tooltip title="View">
                              <IconButton
                                sx={{ color: 'text.primary' as any, '&:hover': { backgroundColor: 'action.hover' }, }}
                                onClick={(e) => {
                                  setStates(v => ({
                                    ...v,
                                    recordToEdit: d,
                                    openEdit: true,
                                  }))
                                }}
                              >
                                <ArrowForwardIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )
                      }}
                    />
                  </Datagrid>
                </Box>
              </Scrollbar>
              {//@ts-ignore 
              <DefaultPagination {...listCtx} />}
            </Card>
            <CreateOrEdit resource={policyResource} basePath="" record={states.recordToEdit} onFailure={savedHandlers.onFailure} onSuccess={savedHandlers.onSuccess.merge(refreshLight)}>
              {states.openEdit && 
                <EditKey 
                  databaseId={databaseInfo?.databaseId}
                  disabled={isOverride || states.recordToEdit?.databaseId === 0 || states.recordToEdit?.isManaged}
                  open={states.openEdit} 
                  setOpen={(x:boolean) => {setStates(v =>({...v, openEdit:x})); if (!x) { setTimeout(() => setStates(v => ({...v, recordToEdit:null}))) }}} 
                />
              }
            </CreateOrEdit>
            {hasWritePermit &&
              <>
                <Confirm
                  isOpen={openConfirm}
                  title={"Delete Token Validation Key"}
                  loading={loading}
                  onConfirm={handleDelete}
                  onClose={(e:any) => {handleDialogClose(e); recordToDel.current = null}}
                  confirm="Confirm"
                  cancel="Cancel"
                  content={(
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ ml: 1 }}
                        >
                          Please confirm whether to proceed with the deletion of this
                          {(recordToDel.current?.name) ? ` "${recordToDel.current?.name}" ` : " "}
                          Token Validation Key.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
                <Create resource="token-validation-key-regenerate" basePath="" {...regenTVKHandlers}>
                  <ConfirmRegenerate d={states.keyToRegenerate} onClose={() => setStates(v => ({...v, keyToRegenerate:null}))} />
                </Create>
              </>}
          </Grid>
        }

      </Grid>
    </Content>
  )
}

export default DatabaseKeysPolicy;

const DatabaseEncryptionKey: FC = () => {
  const hasWritePermit = useHasPermit([KEY_MANAGEMENT])
  const encryptKey = useRecordContext<EncryptionKeyResponseDto&RecPatch>()

  const { data: keysData } = useGetMainList<EncryptionKeyResponseDto&RecPatch>('settings/encryption/keys', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const keys = Object.values(keysData || {}).sort((a, b) => a.id - b.id)
  
  const [keyId, setKeyId] = useState(encryptKey?.encryptionKeyId || '')
  useEffect(() => {
    setKeyId(encryptKey?.encryptionKeyId || '')
  }, [encryptKey?.encryptionKeyId])

  const save = useSimpleSaveAction()
  const peek = usePeekEncryptSettingChangeLocked()
  const { refetch, saving } = useEditContext()

  const encryptSettingChangeLocked = peek(encryptKey?.id)

  const handleSave = () => {
    if (!hasWritePermit || encryptSettingChangeLocked) { return }
    save({ databaseId: encryptKey.id, encryptionKeyId: keyId }, {
      onSuccess: () => {
        refetch && refetch()
      },
    })
  }
  return (
    <Card>
      <CardHeader 
        title={
          <>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              Encryption Key
              <Tooltip title="This key will be used to encrypt the data columns in this database.">
                <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
              </Tooltip>
            </Box>
          </>
        } 
      />
      <Divider />
      <CardContent>
        <Stack direction="row" alignItems="flex-start" >
          <Box sx={{ width: 360,
            ...(!hasWritePermit && {
              '& div.Mui-disabled': disabledColor,
            }),
          }}>
            <Select
              disabled={!hasWritePermit || encryptSettingChangeLocked}
              fullWidth
              value={keyId}
              onChange={(e) => setKeyId(e.target.value)}
            >
              {keys.map(k => (
                <MenuItem key={k.id} value={k.id} >
                  <Typography variant="body1" sx={{ maxWidth: 400, wordBreak: 'keep-all', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', }}>
                    {k.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
          {hasWritePermit && !!keyId && keyId !== encryptKey?.encryptionKeyId &&
            <Box sx={{ ml: 4 }}>
              <Button 
                disabled={saving}
                variant="outlined" 
                color="primary" 
                sx={{ mr: 2 }}
                onClick={() => setKeyId(encryptKey?.encryptionKeyId || '')}
              >
                Cancel
              </Button>
              <Button 
                disabled={saving}
                variant="contained" 
                color="primary"
                onClick={handleSave}
              >
                Change
              </Button>
            </Box>
          }
        </Stack>
      </CardContent>
    </Card>
  )
}

const ConfirmRegenerate : FC<{d:DataAccessPolicyResponseDto|null, onClose:()=>void}> = ({
  d, 
  onClose,
}) => {
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  
  const sendRegenerate = (e:SyntheticEvent) => {
    d && d.databaseId && save(d.databaseId as any, { onSuccess: onClose })
  }
  return (
    <Confirm
      isOpen={!!d && !!d.databaseId}
      loading={saving}
      title={"Regenerate managed Database Token Validation Key"}
      onConfirm={sendRegenerate}
      onClose={onClose}
      confirm="Confirm"
      cancel="Cancel"
      content={(
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Regenerate this key would invalidate all Data Access Token previously signed by the key. 
              Do you wish to regenerate it?
            </Typography>
          </Box>
        </Box>
      )}
    />
    
  )
}
